import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import UtilitiesOverview from './UtilitiesOverview';
import ContractDetails from './Common/ContractDetails';
import SitesWithStreams from './Common/SitesWithStreams';
import OverweightCharges from './Common/OverweightCharges';
import YourDetails from "./Common/YourDetails";
import { formatDate, formatAmount, formatBillingDay, formatSiteAddress } from './Common/formatters';
import DownloadErrorModal, { DOWNLOAD_ERROR_MESSAGE } from './Common/DownloadErrorModal';

import { ReactComponent as WasteTabIcon } from '../../Shared/svgs/waste-ef-magenta.svg';
import { ReactComponent as BillsTabIcon } from '../../Shared/svgs/bill-ef-magenta.svg';
import { ReactComponent as MetersTabIcon } from '../../Shared/svgs/meter-calendar-magenta.svg';
import { ReactComponent as DetailsTabIcon } from '../../Shared/svgs/customer-icon-magenta.svg';
import { ReactComponent as PdfDownloadIcon } from '../../Shared/svgs/pdf-dowload.svg';
import { ReactComponent as DropdownIcon } from '../../Shared/svgs/dropdown.svg';

import { useWasteOverview } from '../../../lib/hooks/useWasteOverview';
import { useWasteCustomerDetails } from '../../../lib/hooks/useWasteCustomerDetails';
import { useWasteBills } from '../../../lib/hooks/useWasteBills';
import { useWasteTransactions } from '../../../lib/hooks/useWasteTransactions';
import { downloadWasteInvoice } from '../../../lib/api/waste-invoice';
import { downloadWasteTransaction } from '../../../lib/api/waste-transaction';
import { getDirectDebitWasteFeatureFlag } from "../../../lib/api/direct-debit-waste";
const TABS = {
    WASTE_OVERVIEW: 'WasteOverview',
    YOUR_BILLS: 'YourBills',
    YOUR_METERS: 'YourMeters',
    YOUR_DETAILS: 'YourDetails',
};

const BILL_TABS = {
    BILLS: 'Bills',
    TRANSACTIONS: 'Transactions',
};

const tabNavigation = {
    [TABS.YOUR_DETAILS]: '/dashboard/main',
};

const safeYearFromDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isFinite(parsed?.getTime()) ? parsed.getFullYear() : null;
};

export default function WasteOverview() {
    const [selectedTab, setSelectedTab] = useState(TABS.WASTE_OVERVIEW);
    const [selectedBillsTab, setSelectedBillsTab] = useState(BILL_TABS.BILLS);
    const [selectedYear, setSelectedYear] = useState('');
    const [downloadErrorMessage, setDownloadErrorMessage] = useState('');
    const [isDownloadErrorOpen, setIsDownloadErrorOpen] = useState(false);
    const [directDebitWasteFeature, setDirectDebitWasteFeature] = useState(false);
    const history = useHistory();

    const { data, loading, error } = useWasteOverview();
    const wasteOverviewData = data?.data ?? null;

    const { data: billsData, loading: billsLoading, error: billsError } = useWasteBills();
    const { data: transactionsData, loading: transactionsLoading, error: transactionsError } = useWasteTransactions();
    const bills = useMemo(
        () => (Array.isArray(billsData?.data) ? billsData.data : billsData?.data?.bills ?? []),
        [billsData]
    );
    const transactions = useMemo(
        () => (Array.isArray(transactionsData?.data) ? transactionsData.data : transactionsData?.data?.transactions ?? []),
        [transactionsData]
    );
    const customerDomainId = billsData?.data?.customerDomainId;

    const currencyFormatter = useMemo(
        () =>
            new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
            }),
        []
    );

    const years = useMemo(() => {
        const billYears = bills.flatMap((bill) => [
            safeYearFromDate(bill.billDateFrom),
            safeYearFromDate(bill.billDateTo),
        ]);
        const transactionYears = transactions.flatMap((transaction) => [
            safeYearFromDate(transaction.date),
        ]);
        return Array.from(new Set(billYears.concat(transactionYears).filter(Boolean))).sort((a, b) => b - a);
    }, [bills, transactions]);

    const billsForYear = useMemo(() => {
        if (!selectedYear) return [];
        const yearNum = Number(selectedYear);
        return bills.filter((bill) =>
            [bill.billDateFrom, bill.billDateTo].some((date) => safeYearFromDate(date) === yearNum)
        );
    }, [bills, selectedYear]);

    const transactionsForYear = useMemo(() => {
        if (!selectedYear) return [];
        const yearNum = Number(selectedYear);
        return transactions.filter((transaction) => safeYearFromDate(transaction.date) === yearNum);
    }, [transactions, selectedYear]);

    useEffect(() => {
        if (years.length === 0) {
            if (selectedYear) {
                setSelectedYear('');
            }
            return;
        }

        const selectedYearNumber = Number(selectedYear);
        const hasSelectedYear = years.includes(selectedYearNumber);
        if (!selectedYear || !hasSelectedYear) {
            setSelectedYear(String(years[0]));
        }
    }, [years, selectedYear]);

    useEffect(() => {
        getDirectDebitWasteFeatureFlag()
            .then((resp) => {
                setDirectDebitWasteFeature(resp.data.data);
            })
            .catch(() => {
                setDirectDebitWasteFeature(false);
            });
    }, []);

    const handleTabClick = useCallback(
        (tabName) => {
            setSelectedTab(tabName);
            const destination = tabNavigation[tabName];
            if (destination) {
                history.push(destination);
            }
        },
        [history]
    );

    const {
        data: wasteDetails,
        loading: wasteDetailsLoading,
        error: wasteDetailsError,
    } = useWasteCustomerDetails();

    const showDownloadError = useCallback(
        (message = DOWNLOAD_ERROR_MESSAGE) => {
            setDownloadErrorMessage(message);
            setIsDownloadErrorOpen(true);
        },
        []
    );

    const handleDownloadClick = useCallback(
        async (bill) => {
            if (!customerDomainId || !bill?.invoiceNumber) {
                console.warn('Missing data required to download waste invoice');
                showDownloadError();
                return;
            }

            try {
                const { blob, contentType, filename } = await downloadWasteInvoice({
                    customerDomainId,
                    invoiceNumber: bill.invoiceNumber,
                    documentFormat: 'pdf',
                });

                const url = URL.createObjectURL(new Blob([blob], { type: contentType }));
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);
                setIsDownloadErrorOpen(false);
                setDownloadErrorMessage('');
            } catch (err) {
                console.error('Download failed!', err);
                showDownloadError();
            }
        },
        [customerDomainId, showDownloadError]
    );

    const handleTransactionDownloadClick = useCallback(
        async (transaction) => {
            if (!transaction?.domainId) {
                console.warn('Missing data required to download waste transaction');
                showDownloadError();
                return;
            }

            try {
                const { blob, contentType, filename } = await downloadWasteTransaction({
                    transactionDomainId: transaction.domainId
                });

                const url = URL.createObjectURL(new Blob([blob], { type: contentType }));
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);
            } catch (err) {
                console.error('Download failed!', err);
                showDownloadError();
            }
        },
        [showDownloadError]
    );

    const handleBillsSubTabClick = useCallback((tabName) => setSelectedBillsTab(tabName), []);

    if (loading) {
        return (
            <section className="waste-overview" id="waste-overview">
                <p>Loading waste overview</p>
            </section>
        );
    }

    if (error) {
        return (
            <section className="waste-overview" id="waste-overview">
                <p>Unable to load waste overview.</p>
            </section>
        );
    }

    const overviewUtility = wasteOverviewData?.utilityOverview ?? null;

    const contracts = wasteOverviewData?.contracts ?? [];
    const contractItems = contracts.map((contract) => ({
        id: contract.contractId || contract.contractReference,
        contractEndDate: formatDate(contract.terminationDate),
        billingDate: formatBillingDay(contract.expectedBillingDay),
        paymentMethod: contract.paymentMethod || '-',
        billingTerm: contract.billingTerms || '-',
    }));

    const sitesFromApi = wasteOverviewData?.sitesWithWasteStreams ?? [];
    const sites = sitesFromApi.map((site) => ({
        address: formatSiteAddress(site.addressesWithTypes?.[0]),
        wasteStreams: site.wasteStreams || [],
    }));

    const showBillsTable =
        selectedTab === TABS.YOUR_BILLS &&
        !billsLoading &&
        !billsError &&
        billsForYear.length > 0;


    const showTransactionsTable =
        selectedTab === TABS.YOUR_BILLS &&
        !transactionsLoading &&
        !transactionsError &&
        transactionsForYear.length > 0;

    return (
        <section className="waste-overview" id="waste-overview">
            <DownloadErrorModal
                open={isDownloadErrorOpen}
                message={downloadErrorMessage}
                onClose={() => setIsDownloadErrorOpen(false)}
            />

            <div className="waste-overview__tabs">
                <button
                    type="button"
                    className={`waste-tab ${selectedTab === TABS.WASTE_OVERVIEW ? 'waste-tab--active' : ''}`}
                    onClick={() => handleTabClick(TABS.WASTE_OVERVIEW)}
                >
                    <WasteTabIcon className="waste-tab__icon" aria-hidden="true" />
                    <span className="waste-tab__label">Waste Overview</span>
                </button>
                <button
                    type="button"
                    className={`waste-tab ${selectedTab === TABS.YOUR_BILLS ? 'waste-tab--active' : ''}`}
                    onClick={() => handleTabClick(TABS.YOUR_BILLS)}
                >
                    <BillsTabIcon className="waste-tab__icon" aria-hidden="true" />
                    <span className="waste-tab__label">Your bills</span>
                </button>
                <button
                    type="button"
                    className={`waste-tab ${selectedTab === TABS.YOUR_METERS ? 'waste-tab--active' : ''}`}
                    onClick={() => handleTabClick(TABS.YOUR_METERS)}
                >
                    <MetersTabIcon className="waste-tab__icon" aria-hidden="true" />
                    <span className="waste-tab__label">Your Additional Charges</span>
                </button>
                <button
                    type="button"
                    className={`waste-tab ${selectedTab === TABS.YOUR_DETAILS ? 'waste-tab--active' : ''}`}
                    onClick={() => handleTabClick(TABS.YOUR_DETAILS)}
                >
                    <DetailsTabIcon className="waste-tab__icon" aria-hidden="true" />
                    <span className="waste-tab__label">Your details</span>
                </button>
            </div>

            {selectedTab === TABS.YOUR_BILLS && (
                <div className="waste-bills">
                    <div className="waste-overview__tabs waste-overview__tabs--sub">
                        <button
                            type="button"
                            className={`waste-tab ${selectedBillsTab === BILL_TABS.BILLS ? 'waste-tab--active' : ''}`}
                            onClick={() => handleBillsSubTabClick(BILL_TABS.BILLS)}
                        >
                            <span className="waste-tab__label">Your bills</span>
                        </button>
                        <button
                            type="button"
                            className={`waste-tab ${selectedBillsTab === BILL_TABS.TRANSACTIONS ? 'waste-tab--active' : ''}`}
                            onClick={() => handleBillsSubTabClick(BILL_TABS.TRANSACTIONS)}
                        >
                            <span className="waste-tab__label">Your transactions</span>
                        </button>
                        <div className="waste-bills__year">
                            <select
                                className="waste-bills__select"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                            >
                                {years.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                            <DropdownIcon className="waste-bills__select-icon" aria-hidden="true" />
                        </div>
                    </div>

                    {selectedBillsTab === BILL_TABS.BILLS && (
                        <>
                            {billsLoading && (
                                <div className="waste-bills__empty">Loading bills...</div>
                            )}

                            {billsError && (
                                <div className="waste-bills__empty">Unable to load bills.</div>
                            )}

                            {!billsLoading && !billsError && billsForYear.length === 0 && (
                                <div className="waste-bills__empty">
                                    No bills to show for {selectedYear || 'the selected year'}.
                                </div>
                            )}

                            {showBillsTable && (
                                <div className="waste-bills__table">
                                    <div className="waste-bills__header">
                                        <div className="waste-bills__col">INVOICE NO.</div>
                                        <div className="waste-bills__col">DATE</div>
                                        <div className="waste-bills__col">BILL PERIOD</div>
                                        <div className="waste-bills__col">TOTAL CHARGE</div>
                                        <div className="waste-bills__col waste-bills__col--download">DOWNLOAD</div>
                                    </div>

                                    {billsForYear.map((bill) => {
                                        const strike = bill.isRemoved;
                                        return (
                                            <div className="waste-bills__row" key={bill.invoiceNumber}>
                                                <div className={`waste-bills__cell${strike ? ' line-through' : ''}`}>{bill.invoiceNumber}</div>
                                                <div className={`waste-bills__cell${strike ? ' line-through' : ''}`}>{formatDate(bill.billDateFrom)}</div>
                                                <div className={`waste-bills__cell${strike ? ' line-through' : ''}`}>
                                                    {formatDate(bill.billDateFrom)} - {formatDate(bill.billDateTo)}
                                                </div>
                                                <div className={`waste-bills__cell${strike ? ' line-through' : ''}`}>
                                                    {currencyFormatter.format(Number(bill.totalCost ?? 0))}
                                                </div>
                                                <div className="waste-bills__cell waste-bills__cell--download">
                                                    <button
                                                        type="button"
                                                        className="waste-bills__download"
                                                        onClick={() => handleDownloadClick(bill)}
                                                        disabled={!customerDomainId}
                                                    >
                                                        <PdfDownloadIcon className="waste-bills__download-icon" aria-hidden="true" />
                                                        <span>PDF</span>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {selectedBillsTab === BILL_TABS.TRANSACTIONS && (
                        <>
                            {transactionsLoading && (
                                <div className="waste-bills__empty">Loading transactions...</div>
                            )}

                            {transactionsError && (
                                <div className="waste-bills__empty">Unable to load transactions.</div>
                            )}

                            {!transactionsLoading && !transactionsError && transactionsForYear.length === 0 && (
                                <div className="waste-bills__empty">
                                    No transactions to show for {selectedYear || 'the selected year'}.
                                </div>
                            )}

                            {showTransactionsTable && (
                                <div className="waste-bills__table">
                                    <div className="waste-bills__header">
                                        <div className="waste-bills__col">TYPE</div>
                                        <div className="waste-bills__col">DATE</div>
                                        <div className="waste-bills__col">AMOUNT</div>
                                        <div className="waste-bills__col">REFERENCE</div>
                                        <div className="waste-bills__col waste-bills__col--download">DOWNLOAD</div>
                                    </div>

                                    {transactionsForYear.map((transaction) => {
                                        const strike = transaction.isRemoved;
                                        return (
                                            <div className="waste-bills__row" key={transaction.domainId}>
                                                <div className={`waste-bills__cell${strike ? ' line-through' : ''}`}>{transaction.transactionType}</div>
                                                <div className={`waste-bills__cell${strike ? ' line-through' : ''}`}>{formatDate(transaction.date)}</div>
                                                <div className={`waste-bills__cell${strike ? ' line-through' : ''}`}>{formatAmount(transaction.amount)}</div>
                                                <div className={`waste-bills__cell${strike ? ' line-through' : ''}`}>{transaction.transactionReference}</div>
                                                <div className="waste-bills__cell waste-bills__cell--download">
                                                    {transaction.isPdfDownloadable &&
                                                        <button
                                                            type="button"
                                                            className="waste-bills__download"
                                                            onClick={() => handleTransactionDownloadClick(transaction)}
                                                            disabled={!customerDomainId}
                                                        >
                                                            <PdfDownloadIcon className="waste-bills__download-icon" aria-hidden="true" />
                                                            <span>PDF</span>
                                                        </button>
                                                    }
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {selectedTab === TABS.WASTE_OVERVIEW && (
                <div className="waste-overview__content">
                    {overviewUtility && <UtilitiesOverview utilities={[overviewUtility]} />}

                    {contractItems.length > 0 && <ContractDetails contracts={contractItems} />}

                    {sites.length > 0 && <SitesWithStreams sites={sites} />}
                </div>
            )}

            {selectedTab === TABS.YOUR_METERS && (
                <div className="waste-overview__content">
                    <OverweightCharges />
                </div>
            )}

            {selectedTab === TABS.YOUR_DETAILS && (
                <div className="waste-overview__content">
                    {wasteDetailsLoading && <p>Loading your details�</p>}
                    {wasteDetailsError && !wasteDetailsLoading && (
                        <p>Unable to load your details.</p>
                    )}
                    {!wasteDetailsLoading && !wasteDetailsError && wasteDetails && (
                        <YourDetails
                            details={wasteDetails}
                            canEditDetails={false}
                            subtitle={
                                <>
                                    These details apply to your waste account only. You
                                    can&apos;t currently update these details here. Need
                                    help? Check out our{' '}
                                    <a
                                        href="https://everflowutilities.com/waste-help-centre"
                                        className="your-details__subtitle-link"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Waste help centre
                                    </a>.
                                </>
                            }
                            directDebit={directDebitWasteFeature}
                        />
                    )}
                </div>
            )}
        </section>
    );
}
